import React, {FunctionComponent, useState} from 'react';
import {FormBlock, IconButton, Input, LabeledButton, Switch, ValidationModal} from '../../../../shared';
import styles from './CustomSourcePreferenceBlock.module.scss';

import {
  useDeleteTrustedSource,
  useGetTrustedSources,
  usePostTrustedSource,
  usePutTrustedSourceConfiguration
} from '../../../../../hooks/queries/admin/trustedSource';

import type {TrustedSource} from '../../../../../model/TrustedSource';
import {SkeletonSourceItem} from '../SkeletonSourceItem';

// Constants
const TEXT = {
  cancel: 'Confirmez-vous l’annulation de votre saisie ?',
  submit: 'Confirmez-vous que votre saisie est correcte ?',
  delete: 'Confirmez-vous la suppression de la pièce justificative ?',
  empty: ''
};
export const CustomSourcePreferenceBlock: FunctionComponent = () => {
  const [newSource, setNewSource] = useState('');
  const [isAdding, setIsAdding] = useState(false);


  const [validationModal, setValidationModal] = useState({
    visible: false,
    isCancel: false
  });

  const [deletionModal, setDeletionModal] = useState({
    visible: false,
    id: 0,
    text: TEXT.empty
  });

  // API hooks
  const {query: {isLoading, data}} = useGetTrustedSources();
  const {mutate: createTrustedSource} = usePostTrustedSource();
  const {mutate: deleteTrustedSource} = useDeleteTrustedSource();
  const {mutate: updateTrustedSourceConfiguration} = usePutTrustedSourceConfiguration();

  const {customSources} = data || {};

  // === Handlers ===

  const resetValidationModal = () => {
    setValidationModal({
      visible: false,
      isCancel: false
    });
  };

  const resetNewSource = () => {
    setNewSource('');
    setIsAdding(false);
    resetValidationModal();
  };

  const handleNewSourceSubmit = () => {
    if (!newSource) return;

    setValidationModal({
      visible: true,
      isCancel: false,
    });
  };

  const handleCancelNewSource = () => {
    if (!newSource) return resetNewSource();

    setValidationModal({
      visible: true,
      isCancel: true,
    });
  };

  const handleDeleteSource = (id: number, value: string) => {
    setDeletionModal({visible: true, id, text: value});
  };

  const confirmDeleteSource = () => {
    if (!deletionModal.id) return;

    deleteTrustedSource(deletionModal.id, {
      onSuccess: () =>
        setDeletionModal({visible: false, id: 0, text: TEXT.empty})
    });
  };

  const handleToggleSource = (configurationId: number, isEnabled: boolean) => {
    updateTrustedSourceConfiguration({configurationId, isEnabled});
  };

  // === Render ===

  return (
    <>
      {/* Add/Cancel Modal */}
      <ValidationModal
        display={validationModal.visible}
        title={validationModal.isCancel ? TEXT.cancel : TEXT.submit}
        description={!validationModal.isCancel ? newSource : undefined}
        icon={!validationModal.isCancel ? 'checkCircle' : undefined}
        confirmLabel={!validationModal.isCancel ? 'Ajouter' : 'Confirmer'}
        confirmTheme={!validationModal.isCancel ? 'secondary' : 'invalid'}
        onSubmit={() => {
          if (!validationModal.isCancel) {
            createTrustedSource({value: newSource} as TrustedSource, {
              onSuccess: resetNewSource
            });
          } else {
            resetNewSource()
          }
          resetValidationModal();
        }}
        onClose={resetValidationModal}
        testId="modal-validation-add-source"
      />

      {/* Delete Modal */}
      <ValidationModal
        display={deletionModal.visible}
        title={TEXT.delete}
        description={deletionModal.text}
        confirmLabel="Confirmer"
        confirmTheme="invalid"
        onSubmit={confirmDeleteSource}
        onClose={() =>
          setDeletionModal({visible: false, id: 0, text: TEXT.empty})
        }
        testId="modal-confirm-deletion-source"
      />

      <FormBlock label="Pièces justificatives personnalisées" icon="emptyStar">
        {/* Existing custom sources */}
        {!isLoading &&
          customSources?.map(({id, value, configuration}, index) =>
            value ? (
              <div key={id} className={styles.switchCustomSourceContainer}>
                <Switch
                  name={value}
                  borderBottom
                  checked={configuration.isEnabled}
                  onChange={(checked) =>
                    handleToggleSource(configuration.id, checked)
                  }
                  testId={`switch-custom_source_accepted-${index}`}
                />
                <IconButton
                  icon="trash"
                  title="Supprimer"
                  appearance="standalone"
                  theme="transparent-dark"
                  size="nano"
                  onClick={() => handleDeleteSource(id, value)}
                  testId="button-userForm-cancel-adding-new-source"
                />
              </div>
            ) : null
          )}

        {/* Input for new source */}
        {isAdding && !isLoading && (
          <div className={styles.inputNewSourceContainer}>
            <Input
              name="newSource"
              value={newSource}
              type="text"
              height="small"
              onChange={(e) => setNewSource(e.target.value || '')}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleNewSourceSubmit();
                }
              }}
              colorPreset="light"
              label=""
            />
            <IconButton
              icon="crossCircle"
              title="Annuler"
              noBorder
              appearance="standalone"
              theme="transparent-dark"
              size="micro"
              onClick={handleCancelNewSource}
              testId="button-userForm-cancel-adding-new-source"
            />
            <IconButton
              icon="checkCircle"
              title="Ajouter"
              noBorder
              disabled={!newSource.length}
              appearance="standalone"
              theme="transparent-dark"
              size="micro"
              onClick={handleNewSourceSubmit}
              testId="button-userForm-validate-adding-new-source"
            />
          </div>
        )}

        {/* Add button */}
        {!isAdding && !isLoading && (
          <div className={styles.addCustomSourceBtnContainer}>
            <LabeledButton
              size="small"
              label="Pièce justificative"
              icon="add"
              onClick={() => setIsAdding(true)}
              testId="button-users_form_add_custom_source"
            />
          </div>
        )}

        {/* Skeletons */}
        {isLoading &&
          [0, 1, 2].map((value) => <SkeletonSourceItem key={value}/>)}
      </FormBlock>
    </>
  );
};
